import { useMemo } from 'react'

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { PreviewField } from '../../services/CsvImportService'

type Props = {
	csvFields: string[][]
	hasHeaderRow: boolean
	previewField: PreviewField
	setSpecificPreviewField: (columnIndex: number) => void
	tempHeader: string[]
}

const FieldPicker = ({ csvFields, hasHeaderRow, previewField, setSpecificPreviewField, tempHeader }: Props) => {
	const selectedValues = useMemo(() => {
		if (previewField.columnIndex !== undefined) {
			const tempIndex = previewField.columnIndex
			return (hasHeaderRow ? csvFields.slice(1) : csvFields).map(row => row[tempIndex])
		}
		return []
	}, [csvFields, hasHeaderRow, previewField.columnIndex])
	if (!csvFields.length) {
		return null
	}

	return (
		<Card>
			<CardHeader>
				{!previewField.required && (
					<Badge className="w-min" variant="secondary">
						optional
					</Badge>
				)}
				<div className="flex justify-between space-x-2">
					<CardTitle>In Hit Em Up</CardTitle>
					<CardTitle>Your File</CardTitle>
				</div>
			</CardHeader>
			<CardContent>
				<div className="grid grid-cols-5 text-center items-center">
					<p className="col-span-2">{previewField.field}</p>
					<p>=</p>
					<Select
						onValueChange={value => {
							const selectedIndex = tempHeader.findIndex(field => field === value)
							setSpecificPreviewField(selectedIndex)
						}}
					>
						<SelectTrigger className="col-span-2">
							<SelectValue placeholder="Select a field..." />
						</SelectTrigger>
						<SelectContent>
							{tempHeader.map(field => (
								<SelectItem key={field} value={field}>
									{field}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</CardContent>
			{selectedValues.length !== 0 && (
				<CardFooter className="flex-col">
					<p className="mb-2 self-end">{`Found ${selectedValues.length} values`}</p>
					<ScrollArea className="w-96 whitespace-nowrap pb-4">
						<div className="w-max grid grid-rows-4 grid-flow-col gap-1">
							{selectedValues.map((value, index) => (
								<div key={index} className="w-[180px] flex-1">
									<p className="truncate text-end border border-slate-500 rounded-sm px-2">
										{value || '-'}
									</p>
								</div>
							))}
						</div>
						<ScrollBar orientation="horizontal" />
					</ScrollArea>
				</CardFooter>
			)}
		</Card>
	)
}

export default FieldPicker
